import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Button, Modal, ModalContent, ModalFooter, Select, SelectOption } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import {
    ChangeMembershipRoleDocument,
    PortalUserDocument,
    RoleSummaryFragment,
} from '../../graphql/__generated__/types';
import { changeRoleRefusalOf } from './changeRoleRefusal';
import { findRole, refusalRules, roleRefusal } from './onboarding/grantableRoles';

interface Props {
    /** The gesture's subject: the **membership**, never the person. */
    membershipId: string;
    organizationName: string;
    personName: string;
    /** The role this membership carries today: pre-selected, and left visible. */
    currentRoleId: string | null;
    currentRoleName: string;
    /** The portal referential. Backoffice roles are not offered: they refuse here. */
    roles: RoleSummaryFragment[];
    onClose: () => void;
}

/**
 * Changing the role of **one membership**: PCG-332.
 *
 * **Only the role.** Perimeter and closure stay read-only on the sheet; the gesture is
 * unambiguous because it acts on one precise membership, and a person holds one role
 * per account.
 *
 * **A role the actor may not grant stays listed, disabled with its reason** (PCG-65).
 * `grantableByViewer` is judged on the actor's own membership, so the send re-checks
 * it: the picked value stays visible with what changed said next to it, and the server
 * refuses too. The current role is offered pre-selected even when it is not grantable,
 * so it can be read; only a *different* grantable role can be saved.
 */
const ChangeRoleDialog = ({
    membershipId,
    organizationName,
    personName,
    currentRoleId,
    currentRoleName,
    roles,
    onClose,
}: Props) => {
    const [selectedId, setSelectedId] = useState<string | null>(currentRoleId);

    const [changeRole, { loading, error }] = useMutation(ChangeMembershipRoleDocument, {
        // The sheet prints the role of every membership; this changes one.
        refetchQueries: [PortalUserDocument],
        awaitRefetchQueries: true,
    });

    const options: SelectOption<string>[] = roles
        .filter((role) => role.id)
        .map((role) => ({
            label: role.name ?? '',
            value: role.id as string,
            disabled: !role.grantableByViewer,
            disabledReason: role.grantableByViewer ? undefined : roleRefusal(role.notGrantableReason).tag,
        }));

    // Said once under the field, one line per distinct reason: why grey rows are there.
    const rules = refusalRules(roles);
    const chosenRefused = selectedId !== currentRoleId && findRole(roles, selectedId)?.grantableByViewer === false;

    const submit = () => {
        // The refusal is read off `error`; without this a rejection reaches the console
        // as an unhandled one on every refusal.
        changeRole({ variables: { membershipId, roleId: selectedId as string } })
            .then((result) => {
                if (result.data) {
                    onClose();
                }
            })
            .catch(() => {});
    };

    return (
        <Modal open onClose={onClose} title={c('Change role').t`Change the role`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">
                        <b>{personName}</b>
                        {/* The account, always named: the role is held under it, and the
                            person may be attached to several. */}
                        <span className="text-text-600"> · {organizationName}</span>
                    </p>
                    <p className="text-sm text-text-600">
                        {c('Change role').t`Current role:`} <b className="text-text-900">{currentRoleName}</b>
                    </p>

                    <div>
                        <label htmlFor="change-role-select" className="mb-1.5 block text-sm font-medium text-text-900">
                            {c('Change role').t`New role`}
                        </label>
                        <Select
                            id="change-role-select"
                            options={options}
                            value={options.find((option) => option.value === selectedId)}
                            onChange={(option) => setSelectedId(option.value)}
                            placeholder={c('Change role').t`Pick a role`}
                            // An empty list means the roles have not loaded, not that there
                            // are none: say so rather than open a blank sliver.
                            emptyLabel={c('Change role').t`The roles have not loaded. Reopen this sheet.`}
                        />
                        {/* Said once for the whole list, never repeated per row: the
                            qualifier beside a row answers « why that one », the rule
                            answers « why several are grey ». */}
                        {rules.map((rule) => (
                            <p key={rule} className="mt-1.5 text-xs text-text-600">
                                {rule}
                            </p>
                        ))}
                    </div>

                    {error && <RefusalNotice refusal={changeRoleRefusalOf(error)} />}
                </div>
            </ModalContent>

            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button
                    category="default"
                    loading={loading}
                    // Nothing to save when the role is unchanged, and a refused role is
                    // refused at the send anyway: disabled beforehand, with its reason.
                    disabled={!selectedId || selectedId === currentRoleId || chosenRefused}
                    title={chosenRefused ? roleRefusal(findRole(roles, selectedId)?.notGrantableReason).tag : undefined}
                    onClick={submit}
                >
                    {c('Action').t`Save`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ChangeRoleDialog;
